import { useState } from 'react';
import { apiConnectPayouts, apiGetEarnings } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  LoadError,
  Pill,
  Section,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import { FEE_SCHEDULE } from '~/lib/plugin-sdk';
import {
  allowanceUsed,
  apiErrorMessage,
  feeShare,
  formatUsd,
  payoutsStatusMeta,
  STREAM_LABELS,
  streamRows,
} from '~/components/marketplace/developer-model';

/** Gross / fee / net, per listing and recent charges; payouts via Stripe Connect. Amounts are USD cents. */
export function EarningsSection() {
  const earnings = useResource(() => apiGetEarnings(), 'earnings');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = async () => {
    setConnecting(true);
    setError(null);

    try {
      const { url } = await apiConnectPayouts(window.location.href);
      window.location.assign(url);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start payout setup.'));
      setConnecting(false);
    }
  };

  const data = earnings.data;
  const payouts = data ? payoutsStatusMeta(data.payoutsStatus) : null;

  return (
    <Section
      title="Earnings"
      icon="i-ph:currency-circle-dollar"
      aside={payouts && <Pill tone={payouts.tone} label={`Payouts: ${payouts.label}`} />}
    >
      {earnings.error ? (
        <LoadError message="Could not load earnings." onRetry={() => void earnings.reload()} />
      ) : !data ? (
        <SkeletonCard lines={3} />
      ) : (
        <div className={classNames(CARD, 'space-y-4 p-4')}>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Gross" value={formatUsd(data.gross)} />
            <Stat label={`Dunea fee (${feeShare(data.gross, data.platformFee)})`} value={formatUsd(data.platformFee)} />
            <Stat label="Net to you" value={formatUsd(data.net)} strong />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 text-[11.5px]">
              <span className="text-secondary">Founder allowance</span>
              <span className="text-muted [font-variant-numeric:tabular-nums]">
                {data.founderAllowanceRemaining > 0
                  ? `${formatUsd(data.founderAllowanceRemaining)} of ${formatUsd(FEE_SCHEDULE.founderAllowanceCents)} left fee-free`
                  : 'Used. Standard fees apply.'}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-sunken">
              <div
                className="h-full rounded-full bg-accent/70"
                style={{ width: `${Math.round(allowanceUsed(data.founderAllowanceRemaining) * 100)}%` }}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
            <div className="space-y-1.5">
              <h4 className={CAPTION}>By stream</h4>
              <ul className="divide-y divide-hairline">
                {streamRows(data.byStream).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 py-1.5 text-[12px]">
                    <span className={r.net ? 'text-primary' : 'text-muted'}>{r.label}</span>
                    <span className="text-secondary [font-variant-numeric:tabular-nums]">{formatUsd(r.net)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className={CAPTION}>Usage and referrals</h4>
              <Stat label={`On hold (${FEE_SCHEDULE.payoutHoldDays} days)`} value={formatUsd(data.onHold)} />
              <Stat label="Payable at next transfer" value={formatUsd(data.payable)} strong />
              <p className="text-[10.5px] leading-relaxed text-muted">
                Paid once it reaches {formatUsd(FEE_SCHEDULE.minimumPayoutCents)}.
              </p>
            </div>
          </div>

          {data.payoutsStatus !== 'active' && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hairline bg-sunken px-3 py-2.5">
              <span className="text-[12px] text-secondary">
                {data.payoutsStatus === 'restricted'
                  ? 'Stripe needs more details before it can pay you.'
                  : data.payoutsStatus === 'pending'
                    ? 'Stripe is reviewing your payout details.'
                    : 'Set up payouts to sell paid listings and earn from usage and referrals.'}
              </span>
              <button type="button" onClick={() => void connect()} disabled={connecting} className={BTN_SECONDARY}>
                {connecting && <Spinner />}
                {data.payoutsStatus === 'none' ? 'Set up payouts' : 'Continue on Stripe'}
              </button>
            </div>
          )}
          {error && <ErrorBanner message={error} />}

          {data.byListing.length > 0 && (
            <div className="space-y-1.5">
              <h4 className={CAPTION}>By listing</h4>
              <ul className="divide-y divide-hairline">
                {data.byListing.map((l) => (
                  <li key={l.slug} className="flex items-center justify-between gap-3 py-1.5 text-[12px]">
                    <span className="min-w-0 truncate text-primary">{l.name}</span>
                    <span className="shrink-0 text-muted [font-variant-numeric:tabular-nums]">
                      {l.charges} {l.charges === 1 ? 'charge' : 'charges'} · {formatUsd(l.gross)} gross ·{' '}
                      <span className="text-primary">{formatUsd(l.net)} net</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.recent.length > 0 ? (
            <div className="space-y-1.5">
              <h4 className={CAPTION}>Recent</h4>
              <ul className="divide-y divide-hairline">
                {data.recent.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 py-1.5 text-[12px]">
                    <span className="min-w-0 truncate text-secondary">
                      {r.slug ? <code className="font-geist-mono text-[11px]">{r.slug}</code> : STREAM_LABELS[r.stream]}
                      {r.slug && ` · ${STREAM_LABELS[r.stream] ?? r.stream}`} · {formatDate(r.occurredAt)}
                      {r.payableAt && !r.paid && ` · payable ${formatDate(r.payableAt)}`}
                    </span>
                    <span
                      className={classNames(
                        'shrink-0 [font-variant-numeric:tabular-nums]',
                        r.kind === 'refunded' ? 'text-danger' : 'text-primary',
                      )}
                    >
                      {r.kind === 'refunded' ? `−${formatUsd(Math.abs(r.net))} refund` : `+${formatUsd(r.net)}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-[12px] text-muted">No sales yet.</p>
          )}
        </div>
      )}
    </Section>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[10.5px] text-muted">{label}</div>
      <div
        className={classNames(
          'mt-0.5 text-[18px] leading-none [font-variant-numeric:tabular-nums]',
          strong ? 'text-primary' : 'text-secondary',
        )}
      >
        {value}
      </div>
    </div>
  );
}
